import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth } from '@/features/auth/AuthContext'
import {
  checkDataAvailability,
  downloadReport,
  fetchReportTargets,
  fetchYearsWithData,
} from '@/features/monthlyReports/api'
import type { ReportFormat } from '@/features/monthlyReports/types'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'

/** Ports includes/monthly_reports.html — on-demand performance report
 * generation (Excel/Word), distinct from Reports Configuration (scheduling
 * only). Download uses a blob response instead of the legacy hidden-form
 * submit — same backend endpoint/response, just fetched via axios instead
 * of a native form POST, since nothing on the server requires form
 * encoding specifically (confirmed: it reads the same JSON/form-parseable
 * body either way). */
export default function MonthlyReportsPage() {
  const { hasPermission } = useAuth()
  const canDownload = hasPermission('reports.download')
  const targetsQuery = useQuery({ queryKey: ['report-targets'], queryFn: fetchReportTargets })
  const [targetKey, setTargetKey] = useState('')
  const [format, setFormat] = useState<ReportFormat>('xlsx')
  const [selectedYears, setSelectedYears] = useState<Set<number>>(new Set())
  const [message, setMessage] = useState<{ type: 'success' | 'danger' | 'warning'; text: string } | null>(null)

  const target = targetsQuery.data?.find((t) => `${t.kind}|${t.identifier}` === targetKey)

  const yearsQuery = useQuery({
    queryKey: ['report-years', targetKey],
    queryFn: () => fetchYearsWithData(target!.kind, target!.identifier),
    enabled: Boolean(target),
  })

  function onTargetChange(key: string) {
    setTargetKey(key)
    setSelectedYears(new Set())
    setMessage(null)
  }

  const previewMutation = useMutation({
    mutationFn: () =>
      checkDataAvailability({ kind: target!.kind, identifier: target!.identifier, format, years: Array.from(selectedYears) }),
    onSuccess: (result) => {
      if (result.success) {
        const missing = result.years_without_data ?? []
        setMessage({
          type: missing.length ? 'warning' : 'success',
          text: missing.length
            ? `Data found for ${result.years_with_data?.join(', ')}. No data for ${missing.join(', ')}. No file is kept from this check.`
            : `Data available for all selected years. No file is kept from this check.`,
        })
      } else {
        setMessage({ type: 'danger', text: result.error ?? 'Check failed.' })
      }
    },
    onError: (err) => setMessage({ type: 'danger', text: err instanceof Error ? err.message : 'Check failed.' }),
  })

  const downloadMutation = useMutation({
    mutationFn: () =>
      downloadReport({ kind: target!.kind, identifier: target!.identifier, format, years: Array.from(selectedYears) }),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    onError: (err) => setMessage({ type: 'danger', text: err instanceof Error ? err.message : 'Download failed.' }),
  })

  function toggleYear(y: number) {
    setSelectedYears((prev) => {
      const next = new Set(prev)
      if (next.has(y)) next.delete(y)
      else next.add(y)
      return next
    })
  }

  if (targetsQuery.isLoading) return <LoadingState label="Loading…" />
  if (targetsQuery.isError) {
    return (
      <ErrorState
        message={targetsQuery.error instanceof Error ? targetsQuery.error.message : 'Failed to load.'}
        onRetry={() => targetsQuery.refetch()}
      />
    )
  }
  if (!targetsQuery.data || targetsQuery.data.length === 0) {
    return <EmptyState message="No active sites or site groups are configured for reporting." />
  }

  const messageColor =
    message?.type === 'success' ? 'text-brand' : message?.type === 'warning' ? 'text-text-primary' : 'text-error'

  return (
    <div className="max-w-lg space-y-4">
      <h1 className="text-2xl font-bold text-ink">Monthly Reports</h1>

      <div className="space-y-3 rounded-2xl border border-nav-line bg-card p-5">
        <div>
          <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Site or Group</label>
          <select
            value={targetKey}
            onChange={(e) => onTargetChange(e.target.value)}
            className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
          >
            <option value="">Select…</option>
            <optgroup label="Sites">
              {targetsQuery.data.filter((t) => t.kind === 'individual').map((t) => (
                <option key={`${t.kind}|${t.identifier}`} value={`${t.kind}|${t.identifier}`}>
                  {t.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Groups">
              {targetsQuery.data.filter((t) => t.kind === 'group').map((t) => (
                <option key={`${t.kind}|${t.identifier}`} value={`${t.kind}|${t.identifier}`}>
                  {t.name} ({t.site_count} sites)
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        <div>
          <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Format</label>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as ReportFormat)}
            className="w-full rounded-xl border border-nav-line bg-input px-3.5 py-2.5 text-sm text-text-primary focus:border-brand focus:outline-none"
          >
            <option value="xlsx">Excel (.xlsx)</option>
            <option value="docx">Word (.docx)</option>
          </select>
        </div>

        {target && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-nav-muted">Years with data</label>
            {yearsQuery.isLoading && <LoadingState label="Checking…" />}
            {yearsQuery.data && yearsQuery.data.length === 0 && (
              <p className="text-sm text-text-secondary">No data found.</p>
            )}
            {yearsQuery.data && yearsQuery.data.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {[...yearsQuery.data].sort((a, b) => b - a).map((y) => (
                  <label key={y} className="flex items-center gap-1 text-sm text-text-primary">
                    <input type="checkbox" checked={selectedYears.has(y)} onChange={() => toggleYear(y)} />
                    {y}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {message && <p className={`text-sm ${messageColor}`}>{message.text}</p>}

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={!target || selectedYears.size === 0 || previewMutation.isPending}
            onClick={() => previewMutation.mutate()}
            className="rounded-full border border-nav-line px-5 py-2 text-sm text-text-primary hover:bg-nav-hover disabled:opacity-50"
          >
            {previewMutation.isPending ? 'Checking…' : 'Check Data Availability'}
          </button>
          {canDownload && (
            <button
              type="button"
              disabled={!target || selectedYears.size === 0 || downloadMutation.isPending}
              onClick={() => downloadMutation.mutate()}
              className="rounded-full bg-brand px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              {downloadMutation.isPending ? 'Downloading…' : 'Download Report'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
